import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { loginUserWithEmailAndPasswordCredentials } from "./signin.service";
import { setItem } from "../../helper/localstorage.helper";
import { updateUser as updateAuthUser, logOut } from "../../../redux/auth/authSlice";

interface ApiResponse {
    statusCode: number;
    message: string;
    response?: {
        userData: [];
    };
}

interface SignInPayload {
    email: string;
    password: string;
}

let user = JSON.parse(localStorage.getItem("user") || 'null');

if (user) {
    if (new Date().getTime() > new Date(user.expireIn).getTime()) {
        user = null;
        localStorage.removeItem("user");
    }
}

const initialState = {
    message: "",
    isError: false,
    isSuccess: false,
    loading: false,
    error: null as string | null,
    user: user?.userData || null,
};

export const signInWithEmailAndPassword = createAsyncThunk(
    "signin/signInWithEmailAndPassword",
    async (user: SignInPayload, thunkAPI) => {
        try {
            const response = await loginUserWithEmailAndPasswordCredentials(user) as ApiResponse;

            if (!(response.statusCode === 200 || response.statusCode === 201)) {
                return thunkAPI.rejectWithValue(response.message);
            }

            if (response.response) {
                setItem("user", JSON.stringify(response.response));
                thunkAPI.dispatch(updateAuthUser(response.response.userData));
                window.dispatchEvent(new Event("userUpdated"));
            }

            return response.response;
        } catch (error: any) {
            const message =
                (error?.response &&
                    error?.response?.data &&
                    error?.response?.data?.message) ||
                error?.message ||
                error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

const signinSlice = createSlice({
    name: "signin",
    initialState,
    reducers: {
        reset: (state) => {
            state.loading = false;
            state.isSuccess = false;
            state.isError = false;
            state.message = "";
            state.error = null;
        },
        clearError: (state) => {
            state.error = null;
            state.isError = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signInWithEmailAndPassword.pending, (state) => {
                state.loading = true;
                state.isSuccess = false;
                state.isError = false;
                state.message = "";
                state.error = null;
            })
            .addCase(signInWithEmailAndPassword.fulfilled, (state, action) => {
                state.loading = false;
                state.isSuccess = true;
                state.user = action.payload?.userData;
                state.error = null;
            })
            .addCase(signInWithEmailAndPassword.rejected, (state, action) => {
                state.loading = false;
                state.isError = (action?.payload === "account_deactivated" || action?.payload === "inactive_account") ? false : true;
                state.message = action.payload as string;
                state.error = action.payload as string;
                state.user = null;
            })
            // Handle auth logOut action to clear signin state
            .addCase(logOut, () => initialState);
    },
});

export const { reset, clearError } = signinSlice.actions;
export default signinSlice.reducer;
